/**
 * 3D Scene for warehouse layout visualization using React Three Fiber
 */

import React, { useRef, useMemo, useState } from 'react';
import { Canvas, useFrame, ThreeEvent } from '@react-three/fiber';
import { OrbitControls, Grid, Text, PerspectiveCamera, Environment } from '@react-three/drei';
import * as THREE from 'three';
import {
  Layout3DSceneProps,
  RackPosition,
  RackDimensions,
  HeatmapData,
  LocationActivity,
  ACTIVITY_COLORS,
  ActivityType
} from '../types/warehouseLayout';

// ============================================
// Rack 3D Component
// ============================================

interface Rack3DProps {
  position: RackPosition;
  dimensions: RackDimensions;
  isSelected: boolean;
  onClick: () => void;
  heatmapIntensity?: number;
  activities?: LocationActivity[];
  showHeatmap: boolean;
  showActivity: boolean;
}

const Rack3D: React.FC<Rack3DProps> = ({
  position,
  dimensions,
  isSelected,
  onClick,
  heatmapIntensity = 0,
  activities = [],
  showHeatmap,
  showActivity
}) => {
  const meshRef = useRef<THREE.Mesh>(null);
  const [hovered, setHovered] = useState(false);

  // Animate on activity
  useFrame((state) => {
    if (meshRef.current && showActivity && activities.length > 0) {
      const pulse = Math.sin(state.clock.elapsedTime * 3) * 0.05;
      meshRef.current.scale.setScalar(1 + pulse);
    } else if (meshRef.current) {
      meshRef.current.scale.setScalar(1);
    }
  });

  // Calculate color
  const color = useMemo(() => {
    if (showHeatmap && heatmapIntensity > 0) {
      const r = Math.round(255 * heatmapIntensity);
      const g = Math.round(100 * (1 - heatmapIntensity));
      const b = Math.round(50 * (1 - heatmapIntensity));
      return `rgb(${r}, ${g}, ${b})`;
    }
    if (activities.length > 0) {
      return ACTIVITY_COLORS[activities[0].activityType as ActivityType] || '#3b82f6';
    }
    return '#3b82f6';
  }, [showHeatmap, heatmapIntensity, activities]);

  // Apply rotation - Three.js handles rotation transform, no need to swap dimensions
  const rotationRadians: [number, number, number] = [0, (position.rotation * Math.PI) / 180, 0];

  return (
    <group position={[position.x, dimensions.height / 2, position.y]} rotation={rotationRadians}>
      {/* Main rack body */}
      <mesh
        ref={meshRef}
        onClick={(e: ThreeEvent<MouseEvent>) => {
          e.stopPropagation();
          onClick();
        }}
        onPointerOver={() => setHovered(true)}
        onPointerOut={() => setHovered(false)}
      >
        <boxGeometry args={[dimensions.width, dimensions.height, dimensions.depth]} />
        <meshStandardMaterial
          color={color}
          transparent
          opacity={hovered ? 0.9 : 0.75}
          metalness={0.1}
          roughness={0.8}
        />
      </mesh>

      {/* Selection outline */}
      {isSelected && (
        <lineSegments>
          <edgesGeometry args={[new THREE.BoxGeometry(dimensions.width + 0.1, dimensions.height + 0.1, dimensions.depth + 0.1)]} />
          <lineBasicMaterial color="#fbbf24" linewidth={2} />
        </lineSegments>
      )}

      {/* Shelf lines */}
      {Array.from({ length: dimensions.shelves }).map((_, i) => {
        const shelfY = -dimensions.height / 2 + (i + 1) * (dimensions.height / dimensions.shelves);
        return (
          <mesh key={i} position={[0, shelfY, 0]}>
            <boxGeometry args={[dimensions.width - 0.02, 0.02, dimensions.depth - 0.02]} />
            <meshStandardMaterial color="#1e3a5f" />
          </mesh>
        );
      })}

      {/* Vertical dividers */}
      {Array.from({ length: dimensions.verticals - 1 }).map((_, i) => {
        const dividerX = -dimensions.width / 2 + (i + 1) * (dimensions.width / dimensions.verticals);
        return (
          <mesh key={i} position={[dividerX, 0, 0]}>
            <boxGeometry args={[0.02, dimensions.height, dimensions.depth - 0.02]} />
            <meshStandardMaterial color="#1e3a5f" opacity={0.5} transparent />
          </mesh>
        );
      })}

      {/* Rack label */}
      <Text
        position={[0, dimensions.height / 2 + 0.3, 0]}
        fontSize={0.4}
        color="white"
        anchorX="center"
        anchorY="middle"
      >
        {position.label || position.rackNumber}
      </Text>

      {/* Activity indicator */}
      {showActivity && activities.length > 0 && (
        <mesh position={[dimensions.width / 2 - 0.2, dimensions.height / 2 + 0.1, dimensions.depth / 2 - 0.2]}>
          <sphereGeometry args={[0.15, 16, 16]} />
          <meshStandardMaterial
            color={ACTIVITY_COLORS[activities[0].activityType as ActivityType] || '#22c55e'}
            emissive={ACTIVITY_COLORS[activities[0].activityType as ActivityType] || '#22c55e'}
            emissiveIntensity={0.5}
          />
        </mesh>
      )}
    </group>
  );
};

// ============================================
// Warehouse Floor Component
// ============================================

interface WarehouseFloorProps {
  length: number;
  width: number;
}

const WarehouseFloor: React.FC<WarehouseFloorProps> = ({ length, width }) => {
  return (
    <group>
      {/* Floor plane */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[length / 2, -0.01, width / 2]} receiveShadow>
        <planeGeometry args={[length, width]} />
        <meshStandardMaterial color="#374151" />
      </mesh>

      {/* Grid helper */}
      <Grid
        args={[length, width]}
        position={[length / 2, 0, width / 2]}
        cellSize={1}
        cellThickness={0.5}
        cellColor="#4b5563"
        sectionSize={5}
        sectionThickness={1}
        sectionColor="#6b7280"
        fadeDistance={100}
        infiniteGrid={false}
      />

      {/* Warehouse boundary */}
      <lineSegments position={[length / 2, 0.01, width / 2]}>
        <edgesGeometry args={[new THREE.PlaneGeometry(length, width)]} />
        <lineBasicMaterial color="#9ca3af" />
      </lineSegments>
    </group>
  );
};

// ============================================
// Main 3D Scene Component
// ============================================

const Layout3DScene: React.FC<Layout3DSceneProps> = ({
  layout,
  rackSummaries,
  rackDimensions,
  editorState,
  onRackSelect,
  heatmapData = [],
  activities = []
}) => {
  const { dimensions, racks } = layout;

  // Create heatmap lookup
  const heatmapLookup = useMemo(() => {
    const lookup = new Map<string, number>();
    heatmapData.forEach(data => {
      lookup.set(data.rackNumber, Math.max(lookup.get(data.rackNumber) || 0, data.intensity));
    });
    return lookup;
  }, [heatmapData]);

  // Create activity lookup
  const activityLookup = useMemo(() => {
    const lookup = new Map<string, LocationActivity[]>();
    activities.forEach(activity => {
      const key = activity.rackNumber;
      if (!lookup.has(key)) {
        lookup.set(key, []);
      }
      lookup.get(key)!.push(activity);
    });
    return lookup;
  }, [activities]);

  // Camera position based on warehouse size
  const cameraPosition: [number, number, number] = [
    dimensions.length / 2,
    Math.max(dimensions.length, dimensions.width) * 0.8,
    dimensions.width + Math.max(dimensions.length, dimensions.width) * 0.5
  ];

  return (
    <div className="w-full h-full bg-gray-900 rounded-lg overflow-hidden">
      <Canvas shadows>
        <PerspectiveCamera
          makeDefault
          position={cameraPosition}
          fov={50}
        />

        {/* Lighting */}
        <ambientLight intensity={0.4} />
        <directionalLight
          position={[dimensions.length, 20, dimensions.width]}
          intensity={0.8}
          castShadow
          shadow-mapSize={[2048, 2048]}
        />
        <pointLight position={[0, 10, 0]} intensity={0.3} />

        {/* Environment for reflections */}
        <Environment preset="warehouse" />

        {/* Controls */}
        <OrbitControls
          target={[dimensions.length / 2, 0, dimensions.width / 2]}
          maxPolarAngle={Math.PI / 2 - 0.1}
          minDistance={5}
          maxDistance={Math.max(dimensions.length, dimensions.width) * 2}
          enableDamping
          dampingFactor={0.05}
        />

        {/* Warehouse floor */}
        <WarehouseFloor length={dimensions.length} width={dimensions.width} />

        {/* Racks */}
        {racks.map(rack => {
          const dim = rackDimensions.get(rack.rackNumber) || {
            width: 2.5,
            depth: 1.0,
            height: 3.0,
            verticals: 5,
            shelves: 5
          };

          return (
            <Rack3D
              key={rack.rackNumber}
              position={rack}
              dimensions={dim}
              isSelected={editorState.selectedRacks.has(rack.rackNumber)}
              onClick={() => onRackSelect(rack.rackNumber)}
              heatmapIntensity={heatmapLookup.get(rack.rackNumber)}
              activities={activityLookup.get(rack.rackNumber)}
              showHeatmap={editorState.showHeatmap}
              showActivity={editorState.showActivity}
            />
          );
        })}

        {/* Dimension labels */}
        <Text
          position={[dimensions.length / 2, 0.5, -1]}
          fontSize={0.8}
          color="#9ca3af"
          anchorX="center"
        >
          {dimensions.length}m
        </Text>
        <Text
          position={[-1, 0.5, dimensions.width / 2]}
          fontSize={0.8}
          color="#9ca3af"
          anchorX="center"
          rotation={[0, Math.PI / 2, 0]}
        >
          {dimensions.width}m
        </Text>

        {/* Origin marker */}
        <mesh position={[0, 0.1, 0]}>
          <sphereGeometry args={[0.2, 16, 16]} />
          <meshStandardMaterial color="#ef4444" />
        </mesh>
      </Canvas>
    </div>
  );
};

export default Layout3DScene;
